import { AdditionalQuestionsEditor } from '@/ui'
import { LandingPageCropControl, LandingPageSimulator } from '@/ui'
import { formSavingAtom, landingPageSavingAtom } from '@/ui'
import { JobiLoader, TimeAgo } from '@/ui'
import { usePrevious } from '@/ui'
import { OuterFormSimulator } from '@/ui/editor/form-simulator'
import { useAtomValue } from 'jotai'
import { motion, PanInfo } from 'motion/react'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type PostLaunchFormEditorShellProps = {
	isLoading: boolean
	backButton: ReactNode
	campaignId: string
}

const DEFAULT_LEFT_COLUMN_WIDTH = 540
const MIN_LEFT_COLUMN_WIDTH = 400

/**
 * Two-column shell for post-launch form editing.
 * Left column: Landing page simulator (sticky)
 * Right column: Form simulator
 */
export function PostLaunchFormEditorShell({
	isLoading,
	backButton,
	campaignId,
}: PostLaunchFormEditorShellProps) {
	const { t } = useTranslation('common')
	const [leftColumnWidth, setLeftColumnWidth] = useState(DEFAULT_LEFT_COLUMN_WIDTH)

	const handleDrag = useCallback((_: unknown, info: PanInfo) => {
		setLeftColumnWidth(Math.max(MIN_LEFT_COLUMN_WIDTH, info.point.x))
	}, [])

	const handleDoubleClick = useCallback(() => {
		setLeftColumnWidth(DEFAULT_LEFT_COLUMN_WIDTH)
	}, [])

	return (
		<div className="flex flex-col h-screen bg-grey-50 relative">
			{/* Header */}
			<div className="flex items-center justify-between px-6 py-4 bg-white border-b border-grey-200">
				<div className="flex items-center gap-4">
					{backButton}
					<h1 className="text-lg font-semibold text-grey-900">{t('postLaunch.editFormTitle')}</h1>
				</div>
				<PostLaunchSaveStatus />
			</div>

			{/* Main content */}
			<div
				className="flex-1 grid relative overflow-hidden"
				style={{
					gridTemplateColumns: `${leftColumnWidth}px 1fr`,
				}}
			>
				{/* Left column: Landing page */}
				<div className="h-full overflow-y-auto bg-white border-r border-grey-200">
					<div className="p-6">
						<h2 className="text-sm font-medium text-grey-500 mb-4 uppercase tracking-wide">
							{t('postLaunch.landingPage')}
						</h2>
						{!isLoading && (
							<div className="flex flex-col gap-6">
								<LandingPageSimulator />
								<div>
									<h3 className="mb-2 text-[10px] font-medium uppercase tracking-wider text-grey-500">
										{t('editor.crop.title')}
									</h3>
									<LandingPageCropControl />
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Right column: Form */}
				<div className="h-full overflow-y-auto bg-white">
					<div className="p-6">
						<h2 className="text-sm font-medium text-grey-500 mb-4 uppercase tracking-wide">
							{t('postLaunch.formQuestions')}
						</h2>
						{!isLoading && <OuterFormSimulator />}
						{!isLoading && <AdditionalQuestionsEditor campaignId={campaignId} />}
					</div>
				</div>

				{/* Draggable divider */}
				<motion.div
					className="absolute inset-y-0 -left-2 w-4 cursor-col-resize z-20 bg-transparent select-none touch-none"
					style={{
						translateX: `${leftColumnWidth}px`,
					}}
					transition={{
						type: 'tween',
						ease: 'linear',
					}}
					onPan={handleDrag}
					onDoubleClick={handleDoubleClick}
				>
					<motion.div className="absolute inset-y-0 w-1 left-1/2 -translate-x-1/2 hover:bg-purple-300 transition-colors" />
				</motion.div>
			</div>

			{/* Loading overlay */}
			{isLoading && (
				<div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
					<div className="flex flex-col items-center gap-4">
						<JobiLoader />
						<div className="text-sm text-grey-600">{t('postLaunch.loadingCampaign')}</div>
					</div>
				</div>
			)}
		</div>
	)
}

/**
 * Simplified save status indicator for post-launch editing.
 * Only tracks form and landing page saving states.
 */
function PostLaunchSaveStatus() {
	const { t } = useTranslation('common')
	const formSaving = useAtomValue(formSavingAtom)
	const landingPageSaving = useAtomValue(landingPageSavingAtom)

	const isSaving = [formSaving, landingPageSaving].some(
		(state) => state === 'debouncing' || state === 'saving',
	)
	const previousIsSaving = usePrevious(isSaving)

	const [lastSaved, setLastSaved] = useState<Date | null>(null)

	useEffect(() => {
		if (previousIsSaving && !isSaving) {
			setLastSaved(new Date())
		}
	}, [isSaving, previousIsSaving])

	return (
		<div className="flex items-center gap-2">
			{isSaving
				? (
					<div className="text-sm text-grey-400 tracking-normal animate-pulse">
						{t('postLaunch.autosaving')}
					</div>
				)
				: (
					<div className="text-sm text-grey-400 tracking-normal">
						{lastSaved
							? (
								<>
									{t('postLaunch.saved')} <TimeAgo date={lastSaved} short ago />
								</>
							)
							: t('postLaunch.allChangesSaved')}
					</div>
				)}
		</div>
	)
}
